/**
 * LanguageSwitcher Component
 *
 * A compact language toggle button for switching between supported languages.
 * Displays current language flag/code and toggles on click.
 */

import { useLanguage } from '@/i18n/hooks/useTypedTranslation';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import { useState } from 'react';
import type { SupportedLanguage } from '@/i18n';

interface LanguageSwitcherProps {
  variant?: 'button' | 'icon';
  size?: 'small' | 'medium';
}

export function LanguageSwitcher({ variant = 'button', size = 'small' }: LanguageSwitcherProps) {
  const { currentLanguage, changeLanguage, supportedLanguages, getCurrentLanguageInfo } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const currentLang = getCurrentLanguageInfo() || supportedLanguages[0];

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (langCode: SupportedLanguage) => {
    changeLanguage(langCode);
    handleClose();
  };

  return (
    <>
      <Button
        variant="outlined"
        size={size}
        onClick={handleClick}
        startIcon={<LanguageIcon sx={{ fontSize: size === 'small' ? 16 : 20 }} />}
        sx={{
          minWidth: variant === 'icon' ? 36 : 'auto',
          px: variant === 'icon' ? 0.5 : 1.5,
          py: 0.5,
          fontSize: size === 'small' ? '0.75rem' : '0.875rem',
          textTransform: 'none',
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}
      >
        {variant !== 'icon' && currentLang.nativeName}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: { minWidth: 140, mt: 0.5 }
          }
        }}
      >
        {supportedLanguages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            selected={lang.code === currentLanguage}
            sx={{ py: 1 }}
          >
            <ListItemText
              primary={lang.nativeName}
              secondary={lang.name !== lang.nativeName ? lang.name : undefined}
              primaryTypographyProps={{ fontSize: '0.875rem' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
            {lang.code === currentLanguage && (
              <CheckIcon sx={{ ml: 1, fontSize: 16, color: 'primary.main' }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default LanguageSwitcher;
